/**
 * Bug report button + modal for submitting issue reports.
 *
 * Features:
 * - Small bug icon button for header/footer integration
 * - Modal with title + description fields
 * - Auto-captures screenshot on modal open (html2canvas, loaded on-demand)
 * - Includes recent console errors/unhandled exceptions in report
 * - Auto-fills pageUrl from window.location
 * - Sends report to POST /api/report-issue
 *
 * Called by:
 * - AppLayout.tsx (header, authenticated pages)
 * - PublicLayout.tsx (footer, public pages)
 */
import { useState, useRef } from 'react';
import { Button, Modal, Input, Form, message, Tooltip, Collapse } from 'antd';
import { BugOutlined, CameraOutlined, LoadingOutlined } from '@ant-design/icons';
import { formatConsoleLogs } from '../utils/consoleCapture';

const { TextArea } = Input;

interface ReportIssueButtonProps {
  /** Button style variant for different placements */
  variant?: 'header' | 'footer';
}

function ReportIssueButton({ variant = 'header' }: ReportIssueButtonProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const screenshotRef = useRef<string | null>(null);
  const [form] = Form.useForm();

  async function captureScreenshot() {
    try {
      setCapturingScreenshot(true);
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        scale: 0.75,
        ignoreElements: (el) => {
          // Skip the modal overlay itself
          return el.classList?.contains('ant-modal-root') ?? false;
        },
      });
      screenshotRef.current = canvas.toDataURL('image/jpeg', 0.6);
    } catch (err) {
      console.error('Screenshot capture failed:', err);
      screenshotRef.current = null;
    } finally {
      setCapturingScreenshot(false);
    }
  }

  function handleOpen() {
    setOpen(true);
    screenshotRef.current = null;
    // Capture screenshot before modal renders over the page
    captureScreenshot();
  }

  async function handleSubmit() {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const consoleLogs = formatConsoleLogs();

      const response = await fetch('/api/report-issue', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: values.title,
          description: values.description,
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
          screenshot: screenshotRef.current,
          consoleLogs: consoleLogs || undefined,
        }),
      });

      const data = await response.json();

      if (data.success) {
        message.success('Bug report submitted — thank you!');
        form.resetFields();
        screenshotRef.current = null;
        setOpen(false);
      } else {
        message.error(data.error || 'Failed to submit report');
      }
    } catch {
      message.error('Failed to submit bug report');
    } finally {
      setLoading(false);
    }
  }

  const consoleLogs = open ? formatConsoleLogs() : '';

  const buttonStyle = variant === 'footer'
    ? {
        color: 'rgba(255, 255, 255, 0.5)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '20px',
        padding: '4px 12px',
        height: 'auto',
        display: 'inline-flex' as const,
        alignItems: 'center' as const,
        gap: '6px',
        fontSize: '0.85rem',
      }
    : {
        color: '#a0a0a0',
        padding: '4px 10px',
        borderRadius: 6,
        display: 'flex' as const,
        alignItems: 'center' as const,
        gap: 6,
        fontSize: '0.85rem',
      };

  return (
    <>
      <Tooltip title="Report a bug">
        <Button
          type="text"
          icon={<BugOutlined />}
          onClick={handleOpen}
          style={buttonStyle}
          onMouseEnter={(e) => (e.currentTarget.style.color = variant === 'footer' ? 'rgba(255,255,255,0.8)' : '#f5f5f5')}
          onMouseLeave={(e) => (e.currentTarget.style.color = variant === 'footer' ? 'rgba(255,255,255,0.5)' : '#a0a0a0')}
        >
          {variant === 'footer' ? 'Report Bug' : null}
        </Button>
      </Tooltip>

      <Modal
        title="Report a Bug"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleSubmit}
        okText="Submit Report"
        confirmLoading={loading}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please provide a brief title' }]}
          >
            <Input placeholder="Brief summary of the issue" maxLength={200} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please describe the issue' }]}
          >
            <TextArea
              placeholder="What happened? What did you expect to happen?"
              rows={4}
              maxLength={2000}
            />
          </Form.Item>

          {/* Screenshot preview */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ color: '#888', fontSize: '0.8rem', marginBottom: 4 }}>
              {capturingScreenshot ? (
                <span><LoadingOutlined style={{ marginRight: 4 }} /> Capturing screenshot...</span>
              ) : screenshotRef.current ? (
                <span><CameraOutlined style={{ marginRight: 4 }} /> Screenshot captured</span>
              ) : (
                <span>Screenshot: not available</span>
              )}
            </div>
            {screenshotRef.current && (
              <img
                src={screenshotRef.current}
                alt="Screenshot preview"
                style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 4, border: '1px solid #333' }}
              />
            )}
          </div>

          {/* Console error log preview */}
          {consoleLogs && (
            <Collapse
              size="small"
              items={[{
                key: 'logs',
                label: <span style={{ fontSize: '0.8rem', color: '#888' }}>Console errors captured ({consoleLogs.split('\n').length})</span>,
                children: (
                  <pre style={{ fontSize: '0.7rem', maxHeight: 120, overflow: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {consoleLogs}
                  </pre>
                ),
              }]}
            />
          )}

          <div style={{ color: '#888', fontSize: '0.8rem', marginTop: 8 }}>
            Page URL, browser info, screenshot, and recent console errors will be included automatically.
          </div>
        </Form>
      </Modal>
    </>
  );
}

export default ReportIssueButton;
